import React, { useState, useEffect } from 'react';
import './DuplicateComparisonModal.css';
import { auth } from '../../services/api';

interface DuplicateComparisonModalProps {
  duplicateInvoice: {
    id: number;
    fileName: string;
    s3Path: string;
    uploadedOn: string;
  };
  originalInvoice: {
    id: number;
    fileName: string;
    s3Path: string;
    uploadedOn: string;
  };
  onClose: () => void;
  onConfirmDuplicate: () => void;
  onIgnoreDuplicate: () => void;
}

const DuplicateComparisonModal: React.FC<DuplicateComparisonModalProps> = ({
  duplicateInvoice,
  originalInvoice,
  onClose,
  onConfirmDuplicate,
  onIgnoreDuplicate
}) => {
  const [processing, setProcessing] = useState(false);
  const [originalPdfUrl, setOriginalPdfUrl] = useState<string | null>(null);
  const [duplicatePdfUrl, setDuplicatePdfUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchPdf = async (s3Path: string, setter: (url: string) => void) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/upload/preview/${encodeURIComponent(s3Path)}`,
          { headers: { ...auth.getAuthHeaders() } }
        );
        if (response.ok) {
          const blob = await response.blob();
          setter(URL.createObjectURL(blob));
        }
      } catch (err) {
        console.error('Error loading PDF:', err);
      }
    };

    fetchPdf(originalInvoice.s3Path, setOriginalPdfUrl);
    fetchPdf(duplicateInvoice.s3Path, setDuplicatePdfUrl);

    return () => {
      if (originalPdfUrl) URL.revokeObjectURL(originalPdfUrl);
      if (duplicatePdfUrl) URL.revokeObjectURL(duplicatePdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalInvoice.s3Path, duplicateInvoice.s3Path]);

  const handleConfirmDuplicate = async () => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/correction/${duplicateInvoice.id}/confirm-duplicate`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...auth.getAuthHeaders() }
        }
      );

      if (!response.ok) throw new Error('Failed to confirm duplicate');

      await response.json();
      onConfirmDuplicate();
    } catch (error) {
      console.error('Error confirming duplicate:', error);
      alert('Failed to confirm duplicate. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleIgnoreDuplicate = () => {
    onIgnoreDuplicate();
  };

  return (
    <div className="duplicate-comparison-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Duplicate Invoice Comparison</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="comparison-container">
            <div className="pdf-section original-section">
              <div className="pdf-header">
                <h3>Original Invoice</h3>
                <div className="invoice-details">
                  <span className="detail-item">
                    <strong>Uploaded on:</strong> {new Date(originalInvoice.uploadedOn).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {originalPdfUrl ? (
                <iframe
                  src={originalPdfUrl}
                  title="Original Invoice PDF"
                  className="pdf-iframe"
                />
              ) : (
                <div className="pdf-iframe" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Loading PDF...
                </div>
              )}
            </div>

            <div className="pdf-section duplicate-section">
              <div className="pdf-header">
                <h3>Duplicate Invoice</h3>
                <div className="invoice-details">
                  <span className="detail-item">
                    <strong>Uploaded on:</strong> {new Date(duplicateInvoice.uploadedOn).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {duplicatePdfUrl ? (
                <iframe
                  src={duplicatePdfUrl}
                  title="Duplicate Invoice PDF"
                  className="pdf-iframe"
                />
              ) : (
                <div className="pdf-iframe" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Loading PDF...
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-ignore"
              onClick={handleIgnoreDuplicate}
              disabled={processing}
            >
              Ignore Duplicate
            </button>
            <button
              className="btn-confirm-duplicate"
              onClick={handleConfirmDuplicate}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Confirm Duplicate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateComparisonModal;
